import { createAsyncThunk, createListenerMiddleware, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from ".";
import { convertAllLinksToRelative, parseLink } from "../utils/linkParser/linkParser";
import jsYaml from "js-yaml";
import { getExportData, k8sKeySort } from "../services/utils";
import { guessTextLanguage } from "../utils/guessTextFormat";

export interface IResourceForEdit {
  kind: string;
  name: string;
  org: string | null;
  gvc: string | null;
}

interface ApplyState {
  previousPath: string | undefined;
  initialContent: string;
  itemLink: string;
  originalResource: IResourceForEdit | undefined;
  orgName: string;
  gvcName: string;
  type: "delete" | "put";
  content: string;
  isLoading: boolean;
  isApplying: boolean;
  error: string;
  language: "json" | "yaml";
}

const initialState: ApplyState = {
  previousPath: "",
  initialContent: "",
  itemLink: "",
  originalResource: undefined,
  orgName: "",
  gvcName: "",
  type: "put",
  content: "",
  isLoading: false,
  isApplying: false,
  error: "",
  language: "yaml",
};

export const fetchItem = createAsyncThunk("apply/fetchItem", async (_, { dispatch, getState }) => {
  const state = getState() as RootState;
  if (!state.apply.itemLink) {
    dispatch(applyActions.setOriginalResource(undefined));
    dispatch(applyActions.setContent(""));
    return;
  }
  const { kind, name, org, gvc, absolute } = parseLink(state.apply.itemLink, { useInputCtx: true });
  const data = await getExportData({ kind, selfLink: absolute }, true);
  dispatch(applyActions.setOriginalResource({ kind, name, org, gvc }));
  const yaml = jsYaml.dump(convertAllLinksToRelative(data), { indent: 2, noRefs: true, sortKeys: k8sKeySort });
  dispatch(applyActions.setContent(yaml));
});

export const applySlice = createSlice({
  name: "apply",
  initialState,
  reducers: {
    initialize: (state, action: PayloadAction<{ itemLink: string; previousPath: string }>) => {
      state.itemLink = action.payload.itemLink;
      state.previousPath = action.payload.previousPath;
      state.type = "put";
      state.isLoading = false;
      state.isApplying = false;
    },
    setType: (state, action: PayloadAction<"delete" | "put">) => {
      state.type = action.payload;
    },
    setOrgName: (state, action: PayloadAction<string>) => {
      state.orgName = action.payload;
    },
    setGvcName: (state, action: PayloadAction<string>) => {
      state.gvcName = action.payload;
    },
    setOriginalResource: (state, action: PayloadAction<IResourceForEdit | undefined>) => {
      state.originalResource = action.payload;
    },
    setContent: (state, action: PayloadAction<string>) => {
      state.content = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setLanguage: (state, action: PayloadAction<"json" | "yaml">) => {
      state.language = action.payload;
    },
    setIsApplying: (state, action: PayloadAction<boolean>) => {
      state.isApplying = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetch item states
      .addCase(fetchItem.pending, (state) => {
        state.isLoading = true;
        state.content = "";
        state.originalResource = undefined;
      })
      .addCase(fetchItem.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(fetchItem.rejected, (state) => {
        state.isLoading = false;
        state.content = "";
        state.originalResource = undefined;
      });
  },
});

export const applyActions = applySlice.actions;

export const applyListenerMiddleware = createListenerMiddleware();

applyListenerMiddleware.startListening({
  actionCreator: applyActions.initialize,
  effect: (action, { getState, dispatch }) => {
    const state = getState() as RootState;
    // set up org name
    dispatch(applyActions.setOrgName(state.context.org || ""));

    let gvcName = state.context.gvc || "";
    // set up gvc state

    if (action.payload.itemLink) {
      const parsedLink = parseLink(action.payload.itemLink, { useInputCtx: true });
      if (parsedLink.isValid) {
        gvcName = parsedLink.gvc;
      }
    }
    dispatch(applyActions.setGvcName(gvcName));
    dispatch(fetchItem());
  },
});
applyListenerMiddleware.startListening({
  actionCreator: applyActions.setContent,
  effect: (action, { getState, dispatch }) => {
    if (!action.payload) {
      dispatch(applyActions.setError(""));
      dispatch(applyActions.setLanguage("yaml"));
      return;
    }

    const { result, error } = guessTextLanguage(action.payload, "yaml");

    dispatch(applyActions.setError(error));
    dispatch(applyActions.setLanguage(result));

    // if (!!error) {
    //   setFileObjects([]);
    // } else {
    //   // @ts-ignore
    //   const objects: any = jsYaml.loadAll(fileText, { json: true });
    //   setFileObjects(objects);
    // }
  },
});

export const applyReducer = applySlice.reducer;
